// Apolo Planning Mode — session-keyed PlanState store. Mirrors agentRunStore: a
// plain module singleton (no React) so the plan survives tab-switch/remount, and any
// mounted SessionWorkspace subscribes to reflect it live. The reducer (loop/planState)
// is the ONLY transition; this store just holds per-session state + fans out updates.

import { useSyncExternalStore } from 'react';
import { initPlanState, reducePlan } from './loop/planState.js';

const plans = new Map(); // sessionId -> { state, subs:Set }

// Plan Mode v2 (E2) — the STRUCTURED pending plan payload from present_plan, keyed
// by session id. PlanState (above) only holds title + ordered todos; this holds the
// richer card payload { summary, steps:[{n,action,tool?,success?}], questions?:[…] }
// that a PlanProposalCard renders (summary line + per-step tool/success + clarifying
// questions). Separate map + subscriptions so it survives tab-switch/remount like the
// run/plan stores. Cleared on resetPlan/clearPendingPlan (e.g. on approve/reject).
const pendingPlans = new Map(); // sessionId -> { plan, subs:Set }

function ensure(id) {
  let p = plans.get(id);
  if (!p) { p = { state: initPlanState(id), subs: new Set() }; plans.set(id, p); }
  return p;
}

function ensurePending(id) {
  let p = pendingPlans.get(id);
  if (!p) { p = { plan: null, subs: new Set() }; pendingPlans.set(id, p); }
  return p;
}

// useSyncExternalStore caching — getSnapshot MUST return the SAME reference until
// the underlying value actually changes, or React warns "getSnapshot should be
// cached to avoid an infinite loop" and may over-render. `getPlanState` for an
// UNKNOWN session used to mint a fresh initPlanState(id) on every call (a new ref
// each time) — that's the footgun. We materialize+cache the entry via ensure(id)
// so the stored `p.state` (a stable ref from the reducer) is what every read
// returns; it's replaced ONLY on an actual transition (dispatchPlan/resetPlan).
export function getPlanState(id) {
  return ensure(id).state;
}

export function subscribePlan(id, cb) {
  const p = ensure(id);
  p.subs.add(cb);
  return () => { p.subs.delete(cb); };
}

/** Dispatch an event through the pure reducer; emit only if state actually changed. */
export function dispatchPlan(id, event) {
  const p = ensure(id);
  const next = reducePlan(p.state, event);
  if (next === p.state) return p.state; // no-op (reducer returned same ref)
  p.state = next;
  for (const cb of p.subs) cb(next);
  return next;
}

export function resetPlan(id) {
  const p = ensure(id);
  p.state = initPlanState(id);
  for (const cb of p.subs) cb(p.state);
  clearPendingPlan(id);
}

/** React binding: re-renders on plan changes for this session. */
export function usePlanState(id) {
  return useSyncExternalStore(
    (cb) => subscribePlan(id, cb),
    () => getPlanState(id),
  );
}

// ── Plan Mode v2 (E2) — structured pending-plan payload (present_plan) ──────────

// Same useSyncExternalStore contract as getPlanState: a stable reference until the
// plan actually changes. `p.plan` is the cached snapshot (null when none) — set/clear
// replace it, so two reads with no mutation in between return the identical ref.
export function getPendingPlan(id) {
  return ensurePending(id).plan;
}

export function subscribePendingPlan(id, cb) {
  const p = ensurePending(id);
  p.subs.add(cb);
  return () => { p.subs.delete(cb); };
}

/** Store the structured plan { summary, steps, questions } for this session. */
export function setPendingPlan(id, plan) {
  const p = ensurePending(id);
  p.plan = plan || null;
  for (const cb of p.subs) cb(p.plan);
  return p.plan;
}

/** Drop the pending plan (e.g. after the user approves/rejects). */
export function clearPendingPlan(id) {
  const p = pendingPlans.get(id);
  if (!p || p.plan === null) return;
  p.plan = null;
  for (const cb of p.subs) cb(p.plan);
}

/** React binding: re-renders when the structured pending plan changes. */
export function usePendingPlan(id) {
  return useSyncExternalStore(
    (cb) => subscribePendingPlan(id, cb),
    () => getPendingPlan(id),
  );
}

// Test-only.
export function __resetPlans() { plans.clear(); pendingPlans.clear(); }
